import React, { useState, useEffect } from 'react';
import {
  Zap, Plus, Edit3, Trash2, ChevronDown, ChevronRight,
  DollarSign, Calendar, Users, CheckCircle, Clock, AlertTriangle
} from 'lucide-react';
import { supabase } from '../supabase';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch { return 'unknown'; }
};

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return '$' + num.toFixed(2);
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TIER_OPTIONS = ['affiliate', 'recruiter', 'director'];

const BONUS_TYPE_LABELS = {
  per_signup: 'Per Signup',
  flat: 'Flat Bonus',
  percentage: 'Percentage'
};

function getSpifStatus(spif) {
  if (!spif.active) return 'inactive';
  const now = new Date();
  const start = spif.start_date ? new Date(spif.start_date) : null;
  const end = spif.end_date ? new Date(spif.end_date) : null;
  if (end && now > end) return 'ended';
  if (start && now < start) return 'upcoming';
  return 'active';
}

const STATUS_STYLES = {
  active: { bg: '#4ecca320', color: '#4ecca3' },
  ended: { bg: '#88888820', color: '#888' },
  upcoming: { bg: '#3498db20', color: '#3498db' },
  inactive: { bg: '#e74c3c20', color: '#e74c3c' }
};

export default function SPIFs() {
  const [spifs, setSpifs] = useState([]);
  const [payoutCounts, setPayoutCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSpif, setEditingSpif] = useState(null);
  const [expandedSpif, setExpandedSpif] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: '',
    description: '',
    bonus_amount: '',
    bonus_type: 'per_signup',
    start_date: '',
    end_date: '',
    target_tiers: [],
    active: true
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: spifsData } = await supabase
        .from('affiliate_spifs')
        .select('*')
        .order('created_at', { ascending: false });

      setSpifs(spifsData || []);

      // Get payout counts per SPIF
      const { data: payoutData } = await supabase
        .from('affiliate_spif_payouts')
        .select('spif_id, id');

      const counts = {};
      (payoutData || []).forEach(p => {
        counts[p.spif_id] = (counts[p.spif_id] || 0) + 1;
      });
      setPayoutCounts(counts);
    } catch (error) {
      console.error('Error loading SPIFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingSpif(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (spif) => {
    setEditingSpif(spif);
    setForm({
      name: spif.name || '',
      description: spif.description || '',
      bonus_amount: spif.bonus_amount || '',
      bonus_type: spif.bonus_type || 'per_signup',
      start_date: spif.start_date || '',
      end_date: spif.end_date || '',
      target_tiers: spif.target_tiers || [],
      active: spif.active !== false
    });
    setShowModal(true);
  };

  const toggleTier = (tier) => {
    setForm(prev => ({
      ...prev,
      target_tiers: prev.target_tiers.includes(tier)
        ? prev.target_tiers.filter(t => t !== tier)
        : [...prev.target_tiers, tier]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Name is required.');
      return;
    }
    if (!form.bonus_amount || parseFloat(form.bonus_amount) <= 0) {
      alert('Bonus amount must be greater than 0.');
      return;
    }

    setSaving(true);
    try {
      const record = {
        name: form.name.trim(),
        description: form.description.trim(),
        bonus_amount: parseFloat(form.bonus_amount),
        bonus_type: form.bonus_type,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        target_tiers: form.target_tiers.length > 0 ? form.target_tiers : null,
        active: form.active
      };

      if (editingSpif) {
        // Update
        await supabase
          .from('affiliate_spifs')
          .update(record)
          .eq('id', editingSpif.id);
      } else {
        // Create
        record.created_by = getAdminEmail();

        const { data: inserted, error } = await supabase
          .from('affiliate_spifs')
          .insert(record)
          .select()
          .single();

        if (error) throw error;

        // Auto-create announcement for new SPIF
        if (inserted) {
          await supabase.from('affiliate_announcements').insert({
            title: `New SPIF: ${form.name.trim()}`,
            body: form.description.trim() || `A new SPIF "${form.name.trim()}" is now available. Bonus: ${formatCurrency(form.bonus_amount)} (${BONUS_TYPE_LABELS[form.bonus_type]}).`,
            target_tiers: form.target_tiers.length > 0 ? form.target_tiers : null,
            priority: 'urgent',
            created_by: getAdminEmail()
          });
        }
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      alert('Error saving SPIF: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (spif) => {
    if (!window.confirm(`Delete SPIF "${spif.name}"? This will also delete all associated payouts.`)) return;

    try {
      // Delete payouts first
      await supabase.from('affiliate_spif_payouts').delete().eq('spif_id', spif.id);
      // Delete SPIF
      await supabase.from('affiliate_spifs').delete().eq('id', spif.id);
      if (expandedSpif === spif.id) setExpandedSpif(null);
      loadData();
    } catch (error) {
      alert('Error deleting SPIF: ' + error.message);
    }
  };

  const togglePayouts = async (spifId) => {
    if (expandedSpif === spifId) {
      setExpandedSpif(null);
      setPayouts([]);
      return;
    }

    setExpandedSpif(spifId);
    setPayoutsLoading(true);

    try {
      const { data } = await supabase
        .from('affiliate_spif_payouts')
        .select(`
          *,
          affiliates (id, name, code)
        `)
        .eq('spif_id', spifId)
        .order('created_at', { ascending: false });

      setPayouts(data || []);
    } catch (error) {
      console.error('Error loading payouts:', error);
      setPayouts([]);
    } finally {
      setPayoutsLoading(false);
    }
  };

  // Summary stats
  const activeCount = spifs.filter(s => getSpifStatus(s) === 'active').length;
  const totalPayouts = Object.values(payoutCounts).reduce((sum, c) => sum + c, 0);

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading SPIFs...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Zap size={28} color="#ff6b35" />
            SPIFs
          </h1>
          <p style={{ color: '#888' }}>
            Sales Performance Incentive Funds — create and manage bonus programs
          </p>
        </div>

        <button
          onClick={openCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Create SPIF
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #ff6b35'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Zap size={18} color="#ff6b35" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Total SPIFs</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#ff6b35',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {spifs.length}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #4ecca3'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <CheckCircle size={18} color="#4ecca3" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Active</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#4ecca3',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {activeCount}
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1.25rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #3498db'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <DollarSign size={18} color="#3498db" />
            <span style={{ color: '#888', fontSize: '0.85rem' }}>Total Payouts</span>
          </div>
          <div style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#3498db',
            fontFamily: '"JetBrains Mono", monospace'
          }}>
            {totalPayouts}
          </div>
        </div>
      </div>

      {/* SPIF List */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflow: 'hidden'
      }}>
        {spifs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
            <Zap size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
            <p>No SPIFs created yet.</p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Click "Create SPIF" to set up a new bonus program.
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500', width: '32px' }}></th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>Name</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>Bonus</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>Date Range</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>Payouts</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>Tiers</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {spifs.map(spif => {
                const status = getSpifStatus(spif);
                const statusStyle = STATUS_STYLES[status];
                const isExpanded = expandedSpif === spif.id;
                const count = payoutCounts[spif.id] || 0;

                return (
                  <React.Fragment key={spif.id}>
                    <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      <td style={{ padding: '1rem 0.5rem 1rem 1rem' }}>
                        <button
                          onClick={() => togglePayouts(spif.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#888',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600', color: '#e0e0e0' }}>{spif.name}</div>
                        {spif.description && (
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {spif.description}
                          </div>
                        )}
                      </td>
                      <td style={{
                        padding: '1rem',
                        textAlign: 'right',
                        fontWeight: '700',
                        color: '#4ecca3',
                        fontFamily: '"JetBrains Mono", monospace'
                      }}>
                        {spif.bonus_type === 'percentage'
                          ? `${parseFloat(spif.bonus_amount)}%`
                          : formatCurrency(spif.bonus_amount)
                        }
                      </td>
                      <td style={{ padding: '1rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                        {BONUS_TYPE_LABELS[spif.bonus_type] || spif.bonus_type}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#888', fontSize: '0.85rem' }}>
                          <Calendar size={14} />
                          <span>
                            {spif.start_date ? formatDate(spif.start_date) : 'No start'}
                            {' - '}
                            {spif.end_date ? formatDate(spif.end_date) : 'Ongoing'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {status}
                        </span>
                      </td>
                      <td style={{
                        padding: '1rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: count > 0 ? '#e0e0e0' : '#555',
                        fontFamily: '"JetBrains Mono", monospace'
                      }}>
                        {count}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {(spif.target_tiers || []).map(tier => (
                            <span key={tier} style={{
                              padding: '0.125rem 0.5rem',
                              background: '#ff6b3515',
                              color: '#ff6b35',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}>
                              {tier}
                            </span>
                          ))}
                          {(!spif.target_tiers || spif.target_tiers.length === 0) && (
                            <span style={{ color: '#555', fontSize: '0.8rem' }}>All</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => openEdit(spif)}
                            style={{
                              padding: '0.375rem',
                              background: '#2a2a2a',
                              border: 'none',
                              borderRadius: '4px',
                              color: '#e0e0e0',
                              cursor: 'pointer'
                            }}
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(spif)}
                            style={{
                              padding: '0.375rem',
                              background: '#2a1a1a',
                              border: 'none',
                              borderRadius: '4px',
                              color: '#e74c3c',
                              cursor: 'pointer'
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Payouts Row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0 }}>
                          <div style={{
                            background: '#0f0f0f',
                            borderTop: '1px solid #2a2a2a',
                            borderBottom: '1px solid #2a2a2a',
                            padding: '1rem 1.5rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.75rem'
                            }}>
                              <DollarSign size={16} color="#ff6b35" />
                              <span style={{ color: '#e0e0e0', fontWeight: '600', fontSize: '0.9rem' }}>
                                Payouts for "{spif.name}"
                              </span>
                            </div>

                            {payoutsLoading ? (
                              <div style={{ color: '#888', padding: '1rem', textAlign: 'center' }}>
                                Loading payouts...
                              </div>
                            ) : payouts.length === 0 ? (
                              <div style={{ color: '#666', padding: '1rem', textAlign: 'center', fontSize: '0.9rem' }}>
                                No payouts recorded for this SPIF yet.
                              </div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                                    <th style={{ textAlign: 'left', padding: '0.625rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' }}>Affiliate</th>
                                    <th style={{ textAlign: 'right', padding: '0.625rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' }}>Amount</th>
                                    <th style={{ textAlign: 'center', padding: '0.625rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' }}>Status</th>
                                    <th style={{ textAlign: 'left', padding: '0.625rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' }}>Paid At</th>
                                    <th style={{ textAlign: 'left', padding: '0.625rem 1rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' }}>Created</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payouts.map(payout => (
                                    <tr key={payout.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                                      <td style={{ padding: '0.625rem 1rem' }}>
                                        <div style={{ fontWeight: '600', color: '#e0e0e0', fontSize: '0.85rem' }}>
                                          {payout.affiliates?.name || 'Unknown'}
                                        </div>
                                        {payout.affiliates?.code && (
                                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{payout.affiliates.code}</div>
                                        )}
                                      </td>
                                      <td style={{
                                        padding: '0.625rem 1rem',
                                        textAlign: 'right',
                                        fontWeight: '700',
                                        color: payout.status === 'paid' ? '#4ecca3' : '#f39c12',
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.85rem'
                                      }}>
                                        {formatCurrency(payout.amount)}
                                      </td>
                                      <td style={{ padding: '0.625rem 1rem', textAlign: 'center' }}>
                                        <span style={{
                                          padding: '0.125rem 0.5rem',
                                          background: payout.status === 'paid' ? '#4ecca320' : '#f39c1220',
                                          color: payout.status === 'paid' ? '#4ecca3' : '#f39c12',
                                          borderRadius: '4px',
                                          fontSize: '0.7rem',
                                          fontWeight: '600',
                                          textTransform: 'uppercase'
                                        }}>
                                          {payout.status}
                                        </span>
                                      </td>
                                      <td style={{ padding: '0.625rem 1rem', color: '#888', fontSize: '0.8rem' }}>
                                        {payout.paid_at ? formatDate(payout.paid_at) : '-'}
                                      </td>
                                      <td style={{ padding: '0.625rem 1rem', color: '#888', fontSize: '0.8rem' }}>
                                        {formatDate(payout.created_at)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '2rem',
            maxWidth: '520px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#e0e0e0',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Zap size={20} color="#ff6b35" />
              {editingSpif ? 'Edit SPIF' : 'Create SPIF'}
            </h2>

            {/* Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. March Madness Signup Blitz"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the SPIF rules and conditions..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Bonus Amount + Type */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                  Bonus Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.bonus_amount}
                  onChange={(e) => setForm({ ...form, bonus_amount: e.target.value })}
                  placeholder={form.bonus_type === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                  Bonus Type
                </label>
                <select
                  value={form.bonus_type}
                  onChange={(e) => setForm({ ...form, bonus_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="per_signup">Per Signup</option>
                  <option value="flat">Flat Bonus</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
            </div>

            {/* Date Range */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Target Tiers */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.85rem' }}>
                Target Tiers
                <span style={{ color: '#666', fontWeight: '400', marginLeft: '0.5rem' }}>
                  (leave empty for all)
                </span>
              </label>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {TIER_OPTIONS.map(tier => {
                  const selected = form.target_tiers.includes(tier);
                  return (
                    <label
                      key={tier}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        background: selected ? '#ff6b3520' : '#2a2a2a',
                        border: `1px solid ${selected ? '#ff6b35' : '#3a3a3a'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: selected ? '#ff6b35' : '#888',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        textTransform: 'capitalize',
                        transition: 'all 0.15s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleTier(tier)}
                        style={{ display: 'none' }}
                      />
                      {selected ? <CheckCircle size={14} /> : <span style={{ width: 14, height: 14, display: 'inline-block', border: '1px solid #555', borderRadius: '3px' }} />}
                      {tier}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Active Toggle */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                color: '#e0e0e0',
                fontSize: '0.85rem'
              }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  style={{ accentColor: '#ff6b35' }}
                />
                Active
              </label>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 2,
                  padding: '0.875rem',
                  background: saving ? '#555' : '#ff6b35',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : (editingSpif ? 'Update SPIF' : 'Create SPIF')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
